import { withScope, captureMessage } from '@sentry/browser';
import { TrackingEvent, Sha256LeadsUserData, LeadsUserData } from '../../../types/consultation';
import { sha256 } from 'js-sha256';
import { tariffBundleMarketingNames } from '../../../types/tariffs';

function getEmptyKeys(payload: TrackingEvent): string[] {
  const emptyKeys: string[] = [];
  const collect = (obj: Record<string, unknown>, prefix = ''): void => {
    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value !== null && typeof value === 'object') {
        collect(value as Record<string, unknown>, path);
      } else if (value === undefined || value === null || value === '') {
        emptyKeys.push(path);
      }
    }
  };
  collect(payload as unknown as Record<string, unknown>);
  return emptyKeys;
}

export async function pushToDataLayer(payload: TrackingEvent): Promise<void> {
  if (payload.leadsUserData && 'email_address' in payload.leadsUserData && payload.leadsUserData.email_address === 'testing-loki+e2e-smoke-live@on-testing.de') {
    return;
  }

  if (payload.leadsUserData && 'email_address' in payload.leadsUserData) {
    const sha256LeadsUserData: Sha256LeadsUserData = {};
    for (const [key, value] of Object.entries(payload.leadsUserData)) {
      if ((key === 'email_address' || key === 'phone_number') && typeof value === 'string') {
        sha256LeadsUserData[('sha256_' + key) as keyof Sha256LeadsUserData] = await hashValues(value, key === 'email_address' ? 'email' : 'phone');
      } else if (key === 'address' && value && typeof value === 'object') {
        sha256LeadsUserData.address = {
          ...(value.first_name && { sha256_first_name: await hashValues(value.first_name) }),
          ...(value.last_name && { sha256_last_name: await hashValues(value.last_name) })
        };
      }
    }
    payload.leadsUserData = sha256LeadsUserData;
  }

  if (payload.tariff && !Object.values(tariffBundleMarketingNames).includes(payload.tariff)) {
    withScope(scope => {
      scope.setLevel('info');
      scope.setTag('tracking', 'empty-values');
      scope.setExtras({
        keys: `invalid tariff: "${payload.tariff}"`,
        event: payload.event ?? 'unknown',
        url: window.location.href
      });
      captureMessage('dataLayer push: tariff not matching marketing tariff name!');
    });
  }

  const emptyKeys = getEmptyKeys(payload);

  if (emptyKeys.length > 0) {
    withScope(scope => {
      scope.setLevel('info');
      scope.setTag('tracking', 'empty-values');
      scope.setExtras({
        keys: emptyKeys.join(', '),
        event: payload.event ?? 'unknown',
        url: window.location.href
      });
      captureMessage('dataLayer push: empty values found!');
    });
    sanitizeEmptyValues(payload as unknown as Record<string, unknown>);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

const DEFAULT_COUNTRY_CALLING_CODE = '49';

// email normalizing LOKI-7883 generating a hash-based identifier for Google
function normalizeEmail(value: string): string {
  const email = value.trim().toLowerCase();
  const atIndex = email.lastIndexOf('@');
  if (atIndex < 1) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  return domain === 'gmail.com' || domain === 'googlemail.com' ? `${local.replace(/\./g, '')}@${domain}` : email;
}

// Lightweight E.164 formatting, +49 default
function normalizePhone(value: string): string {
  let phone = value
    .trim()
    .replace(/\(0\)/g, '')
    .replace(/[^\d+]/g, '');
  if (!phone) {
    return '';
  }
  if (phone.startsWith('+')) {
    phone = phone.replace(/^\+/, '');
  } else if (phone.startsWith('00')) {
    phone = phone.slice(2);
  } else {
    phone = DEFAULT_COUNTRY_CALLING_CODE + phone.replace(/^0/, '');
  }
  phone = phone.replace(/\D/g, '');
  if (!phone) {
    return '';
  }
  return `+${phone}`;
}

type HashType = 'email' | 'phone' | 'text';

async function hashValues(value: string, type: HashType = 'text'): Promise<string> {
  if (value === 'na') return value;
  const normalized = type === 'email' ? normalizeEmail(value) : type === 'phone' ? normalizePhone(value) : value.trim().toLowerCase();
  return sha256(normalized);
}

function sanitizeEmptyValues(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value !== null && typeof value === 'object') {
      sanitizeEmptyValues(value as Record<string, unknown>);
    } else if (value === undefined || value === null || value === '') {
      obj[key] = 'na';
    }
  }
}
